import { computed, ref, watch } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { analyzePdf } from '@/services/Profile/Api';
import {
  getCountries,
  getCountryCities,
  getCountryCitiesByPostCode,
  getPlatformCountries,
} from '@/services/Geo/Api';
import useProfilePreview, {
  profileKeyForSection,
} from '@/state/profile/profilePreview';
import usePageState from '@/state/pageState';
import mapExtractionToPreview from '@/views/profile/composables/mapExtractionToPreview';
import getAxiosResponseErrorCode from '@/utils/getAxiosResponseErrorCode';
import { logWarning } from '@/utils/logger';
import { AxiosError } from 'axios';
import { ProfilePdfAnalysisResponse } from '@/views/profile/models/ProfileExtraction';
import { extractCityName } from '@/utils/user';
import { City } from '@/models/Geo';
import buildReanalyzeUserPrompt, {
  OutputLanguage,
  ReanalyzableProfileSection,
} from '@/views/profile/utils/buildReanalyzeUserPrompt';
import replaceCustomerPrefixPlaceholder from '@/views/profile/utils/replaceCustomerPrefixPlaceholder';

/**
 * Owns the upload-and-extract flow for the Profile-PDF AI upload popup.
 * Calls /v1/users/user/profile/pdf-analysis and writes the mapped result
 * into useProfilePreview.
 */

export type ProfilePdfArea = 'avatar' | 'basic' | 'skills' | 'educations' | 'certificates' | 'projects';

export const ALL_PROFILE_PDF_AREAS: ProfilePdfArea[] = [
  'avatar',
  'basic',
  'skills',
  'educations',
  'certificates',
  'projects',
];

const REANALYZE_SECTION_TO_PROFILE_PDF_AREA: Record<ReanalyzableProfileSection, ProfilePdfArea> = {
  basic: 'basic',
  skills: 'skills',
  educations: 'educations',
  certificates: 'certificates',
  projects: 'projects',
};
const REANALYZE_TIMEOUT_MS = 60000;

export type ProfilePdfReanalyzeSource = {
  pdfBase64?: string | null;
  pdfFileName?: string | null;
};

const useProfilePdfUpload = createGlobalState(() => {
  // Shapes match useFileUpload's output so the popup can mirror its picker
  // result into here without remapping.
  const fileName = ref<string>('');
  const fileContents = ref<string>('');
  const customInstructions = ref('');
  const outputLanguage = ref<OutputLanguage>('Deutsch');
  const selectedProfileAreas = ref<ProfilePdfArea[]>([...ALL_PROFILE_PDF_AREAS]);
  const isSubmitting = ref(false);
  const activeRunId = ref(0);
  const activeAbortController = ref<AbortController | null>(null);
  const isReanalyzing = ref(false);
  const activeReanalyzeSection = ref<ReanalyzableProfileSection | null>(null);
  const reanalyzeRunId = ref(0);
  const reanalyzeAbortController = ref<AbortController | null>(null);
  const reanalyzePdfBase64 = ref('');
  const reanalyzePdfFileName = ref('');
  const lastSuccessfulOutputLanguage = ref<OutputLanguage>('Deutsch');
  const hasReanalyzeSource = computed(() => !!reanalyzePdfBase64.value && !!reanalyzePdfFileName.value);

  const {
    setPreview,
    setPreviewSection,
    setAvatarPreview,
    hasAnyUnresolved,
  } = useProfilePreview();
  const { globalErrorMessage } = usePageState();

  const clearReanalyzePdfSource = () => {
    reanalyzePdfBase64.value = '';
    reanalyzePdfFileName.value = '';
  };

  watch(hasAnyUnresolved, (hasUnresolved) => {
    if (!hasUnresolved) clearReanalyzePdfSource();
  });

  const isAbortError = (error: unknown): boolean => {
    const axiosError = error as AxiosError | undefined;
    const baseError = error as Error | undefined;

    return axiosError?.code === 'ERR_CANCELED'
      || baseError?.name === 'CanceledError'
      || baseError?.name === 'AbortError';
  };

  const mapAvatarImageType = (imageExt?: string): string => {
    if (!imageExt) return 'image/png';
    if (imageExt.startsWith('image/')) return imageExt;

    switch (imageExt.toLowerCase()) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'svg':
      case 'svg+xml':
        return 'image/svg+xml';
      case 'png':
      default:
        return 'image/png';
    }
  };

  const mapAvatarPreview = (response: ProfilePdfAnalysisResponse) => {
    if (!response.avatar?.hasAvatar || !response.avatar.imageBytesBase64) return null;

    return {
      imageData: response.avatar.imageBytesBase64,
      imageType: mapAvatarImageType(response.avatar.imageExt),
      avatarPage: response.avatar.avatarPage,
    };
  };

  const resolveCountryIsoCode = (
    response: ProfilePdfAnalysisResponse,
    platformCountries: Awaited<ReturnType<typeof getPlatformCountries>>,
    geoCountries: Awaited<ReturnType<typeof getCountries>>,
  ) => {
    const rawCountry = response.basic?.country?.trim();
    const normalizedCountry = rawCountry?.toLowerCase();
    const countryAliases: Record<string, string> = {
      germany: 'DE',
      deutschland: 'DE',
      austria: 'AT',
      österreich: 'AT',
      switzerland: 'CH',
      schweiz: 'CH',
    };

    return response.basic?.countryIsoCode
      || countryAliases[normalizedCountry ?? '']
      || platformCountries.find((country) => (
        country.isoCode.toLowerCase() === normalizedCountry
        || country.countryName.toLowerCase() === normalizedCountry
      ))?.isoCode
      || geoCountries.find((country) => (
        country.isoCode.toLowerCase() === normalizedCountry
        || country.countryName.toLowerCase() === normalizedCountry
      ))?.isoCode;
  };

  const parsePostalCity = (countryIsoCode?: string | null, city?: string | null) => {
    if (!countryIsoCode || !city) return null;

    const match = city.trim().match(/^(\d{4,5}),\s*(.+)$/);
    if (!match) return null;

    const [, postalCode, placeName] = match;
    let expectedPostalLength: number | null = null;
    if (countryIsoCode === 'DE') expectedPostalLength = 5;
    if (['AT', 'CH'].includes(countryIsoCode)) expectedPostalLength = 4;
    if (!expectedPostalLength || postalCode.length !== expectedPostalLength) return null;

    return {
      postalCode,
      placeName: placeName.trim(),
    };
  };

  const normalizePlaceName = (value: string) => value
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[.,/()-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const findBestPostalCityMatch = (candidates: City[], placeName: string): City | null => {
    if (!candidates.length || !placeName) return null;

    const normalizedTarget = normalizePlaceName(placeName);
    const scoredCandidates = candidates.map((candidate) => {
      const rawCandidate = candidate.placeName.trim().toLowerCase();
      const normalizedCandidate = normalizePlaceName(candidate.placeName);
      let score = 0;

      if (rawCandidate === placeName.trim().toLowerCase()) score = 100;
      else if (normalizedCandidate === normalizedTarget) score = 90;
      else if (
        normalizedCandidate.startsWith(`${normalizedTarget} `)
        || normalizedTarget.startsWith(`${normalizedCandidate} `)
      ) score = 75;
      else if (
        normalizedCandidate.includes(normalizedTarget)
        || normalizedTarget.includes(normalizedCandidate)
      ) score = 60;

      return { candidate, score };
    }).sort((a, b) => b.score - a.score);

    const bestMatch = scoredCandidates[0];
    const nextBestMatch = scoredCandidates[1];

    if (!bestMatch || bestMatch.score < 60) return null;
    if (nextBestMatch && bestMatch.score === nextBestMatch.score) return null;

    return bestMatch.candidate;
  };

  const normalizeBasicExtraction = async (
    response: ProfilePdfAnalysisResponse,
    signal?: AbortSignal,
  ) => {
    if (!response.basic) return response;

    const [platformCountries, geoCountries] = await Promise.all([
      getPlatformCountries(signal),
      getCountries(signal),
    ]);
    const resolvedCountryIsoCode = resolveCountryIsoCode(response, platformCountries, geoCountries);
    const parsedPostalCity = parsePostalCity(resolvedCountryIsoCode, response.basic.city);
    const cityFilter = (
      parsedPostalCity?.placeName
      ?? extractCityName(response.basic.city ?? '').trim()
    );
    let cityCandidates: City[] = [];
    if (parsedPostalCity && resolvedCountryIsoCode) {
      cityCandidates = await getCountryCitiesByPostCode(
        resolvedCountryIsoCode,
        parsedPostalCity.postalCode,
        signal,
      ) ?? [];
    } else if (resolvedCountryIsoCode && cityFilter) {
      cityCandidates = await getCountryCities(resolvedCountryIsoCode, cityFilter, signal) ?? [];
    }
    const cityResult = parsedPostalCity
      ? findBestPostalCityMatch(
        cityCandidates.filter((candidate) => candidate.postalCode === parsedPostalCity.postalCode),
        parsedPostalCity.placeName,
      )
      : cityCandidates[0];
    let normalizedCity = response.basic.city;
    if (cityResult) {
      normalizedCity = `${cityResult.postalCode}, ${cityResult.placeName}`;
    } else if (parsedPostalCity) {
      normalizedCity = `${parsedPostalCity.postalCode}, ${parsedPostalCity.placeName}`;
    }

    return {
      ...response,
      basic: {
        ...response.basic,
        countryIsoCode: resolvedCountryIsoCode,
        city: normalizedCity,
      },
    };
  };

  const filterAnalysisResponse = (
    response: ProfilePdfAnalysisResponse,
    selectedAreas: ProfilePdfArea[],
  ): ProfilePdfAnalysisResponse => {
    const hasSelectedArea = (area: ProfilePdfArea) => selectedAreas.includes(area);

    return {
      ...response,
      basic: hasSelectedArea('basic') ? response.basic : undefined,
      skills: hasSelectedArea('skills') ? response.skills : [],
      educations: hasSelectedArea('educations') ? response.educations : [],
      certificates: hasSelectedArea('certificates') ? response.certificates : [],
      experiences: hasSelectedArea('projects') ? response.experiences : undefined,
      avatar: hasSelectedArea('avatar') ? response.avatar : undefined,
    };
  };

  const replaceExperiencesTextPlaceholders = (
    response: ProfilePdfAnalysisResponse,
    language: OutputLanguage = outputLanguage.value,
  ): ProfilePdfAnalysisResponse => {
    const experiencesText = response.experiences?.experiencesText;
    if (!experiencesText) return response;

    return {
      ...response,
      experiences: {
        ...response.experiences,
        experiencesText: replaceCustomerPrefixPlaceholder(experiencesText, language),
      },
    };
  };

  const clearFile = () => {
    fileName.value = '';
    fileContents.value = '';
  };

  const reset = () => {
    clearFile();
    customInstructions.value = '';
    outputLanguage.value = 'Deutsch';
    selectedProfileAreas.value = [...ALL_PROFILE_PDF_AREAS];
    isSubmitting.value = false;
    if (!hasAnyUnresolved.value) clearReanalyzePdfSource();
  };

  const buildEffectiveUserPrompt = () => {
    const hiddenPrompt = `Output language: ${outputLanguage.value}.`;
    const visiblePrompt = customInstructions.value.trim();

    if (!visiblePrompt) return hiddenPrompt;
    return `${visiblePrompt}\n${hiddenPrompt}`;
  };

  const submit = async (): Promise<boolean> => {
    if (!fileName.value || isSubmitting.value || isReanalyzing.value || !selectedProfileAreas.value.length) return false;
    const currentFileName = fileName.value;
    const currentFileContents = fileContents.value;
    const currentPrompt = buildEffectiveUserPrompt();
    const selectedAreasSnapshot = [...selectedProfileAreas.value];
    const runId = activeRunId.value + 1;
    const abortController = new AbortController();

    activeRunId.value = runId;
    activeAbortController.value?.abort();
    activeAbortController.value = abortController;
    isSubmitting.value = true;
    try {
      const response = await analyzePdf(
        currentFileContents,
        currentFileName,
        currentPrompt,
        'CREATE',
        abortController.signal,
      );
      if (runId !== activeRunId.value) return false;
      const normalizedResponse = await normalizeBasicExtraction(response, abortController.signal);
      if (runId !== activeRunId.value) return false;
      const responseWithReplacedPlaceholders = replaceExperiencesTextPlaceholders(normalizedResponse);
      const filteredResponse = filterAnalysisResponse(responseWithReplacedPlaceholders, selectedAreasSnapshot);
      if (filteredResponse.warnings?.length) {
        logWarning('Profile PDF analysis warnings:', filteredResponse.warnings);
      }
      if (runId !== activeRunId.value) return false;
      setAvatarPreview(mapAvatarPreview(filteredResponse));
      setPreview(mapExtractionToPreview(
        filteredResponse,
        selectedAreasSnapshot.includes('projects') ? currentFileName : '',
        selectedAreasSnapshot.includes('projects') ? currentFileContents : '',
      ));
      reanalyzePdfBase64.value = currentFileContents;
      reanalyzePdfFileName.value = currentFileName;
      lastSuccessfulOutputLanguage.value = outputLanguage.value;
      return true;
    } catch (error) {
      if (runId !== activeRunId.value || isAbortError(error)) return false;
      globalErrorMessage.value = {
        i18Key: 'profilePdfUpload.errors.uploadFailed',
        code: getAxiosResponseErrorCode(error as AxiosError) ?? 'UNKNOWN_ERROR',
        component: 'useProfilePdfUpload.ts',
        withoutReload: true,
        timeoutMs: 15000,
      };
      return false;
    } finally {
      if (activeAbortController.value === abortController) {
        activeAbortController.value = null;
      }
      if (runId === activeRunId.value) {
        isSubmitting.value = false;
      }
    }
  };

  const reanalyzeSection = async (
    section: ReanalyzableProfileSection,
    fallbackSource: ProfilePdfReanalyzeSource = {},
  ): Promise<boolean> => {
    const pdfBase64 = reanalyzePdfBase64.value || fallbackSource.pdfBase64 || '';
    const pdfFileName = reanalyzePdfFileName.value || fallbackSource.pdfFileName || '';

    if (!pdfBase64 || !pdfFileName || isSubmitting.value || isReanalyzing.value) return false;

    const currentOutputLanguage = lastSuccessfulOutputLanguage.value;
    const selectedArea = REANALYZE_SECTION_TO_PROFILE_PDF_AREA[section];
    const runId = reanalyzeRunId.value + 1;
    const abortController = new AbortController();

    reanalyzeRunId.value = runId;
    reanalyzeAbortController.value?.abort();
    reanalyzeAbortController.value = abortController;
    activeReanalyzeSection.value = section;
    isReanalyzing.value = true;

    try {
      let timeoutId: ReturnType<typeof window.setTimeout> | null = null;
      const timeoutPromise = new Promise<boolean>((resolve) => {
        timeoutId = window.setTimeout(() => {
          resolve(false);
          if (runId !== reanalyzeRunId.value) return;
          reanalyzeRunId.value += 1;
          abortController.abort();
          if (reanalyzeAbortController.value === abortController) {
            reanalyzeAbortController.value = null;
          }
          activeReanalyzeSection.value = null;
          isReanalyzing.value = false;
        }, REANALYZE_TIMEOUT_MS);
      });
      const reanalysisPromise = (async () => {
        const response = await analyzePdf(
          pdfBase64,
          pdfFileName,
          buildReanalyzeUserPrompt(currentOutputLanguage, section),
          'CREATE',
          abortController.signal,
        );
        if (runId !== reanalyzeRunId.value) return false;
        const normalizedResponse = await normalizeBasicExtraction(response, abortController.signal);
        if (runId !== reanalyzeRunId.value) return false;
        const responseWithReplacedPlaceholders = replaceExperiencesTextPlaceholders(
          normalizedResponse,
          currentOutputLanguage,
        );
        const filteredResponse = filterAnalysisResponse(responseWithReplacedPlaceholders, [selectedArea]);
        if (filteredResponse.warnings?.length) {
          logWarning('Profile PDF reanalysis warnings:', filteredResponse.warnings);
        }
        if (runId !== reanalyzeRunId.value) return false;

        const mappedPreview = mapExtractionToPreview(
          filteredResponse,
          section === 'projects' ? pdfFileName : '',
          section === 'projects' ? pdfBase64 : '',
        );

        const mappedSection = mappedPreview[profileKeyForSection(section)];
        if (mappedSection === undefined) return false;

        setPreviewSection(section, mappedSection);
        return true;
      })();
      try {
        return await Promise.race([reanalysisPromise, timeoutPromise]);
      } finally {
        if (timeoutId) window.clearTimeout(timeoutId);
      }
    } catch (error) {
      if (runId !== reanalyzeRunId.value || isAbortError(error)) return false;
      globalErrorMessage.value = {
        i18Key: 'profilePdfUpload.errors.uploadFailed',
        code: getAxiosResponseErrorCode(error as AxiosError) ?? 'UNKNOWN_ERROR',
        component: 'useProfilePdfUpload.ts',
        withoutReload: true,
        timeoutMs: 15000,
      };
      return false;
    } finally {
      if (reanalyzeAbortController.value === abortController) {
        reanalyzeAbortController.value = null;
      }
      if (runId === reanalyzeRunId.value) {
        activeReanalyzeSection.value = null;
        isReanalyzing.value = false;
      }
    }
  };

  const cancelReanalyze = () => {
    reanalyzeRunId.value += 1;
    reanalyzeAbortController.value?.abort();
    reanalyzeAbortController.value = null;
    activeReanalyzeSection.value = null;
    isReanalyzing.value = false;
  };

  const cancel = () => {
    activeRunId.value += 1;
    activeAbortController.value?.abort();
    activeAbortController.value = null;
    isSubmitting.value = false;
  };

  return {
    fileName,
    fileContents,
    customInstructions,
    outputLanguage,
    selectedProfileAreas,
    isSubmitting,
    isReanalyzing,
    activeReanalyzeSection,
    hasReanalyzeSource,
    submit,
    reanalyzeSection,
    cancelReanalyze,
    cancel,
    reset,
    clearFile,
  };
});

export default useProfilePdfUpload;
